import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Users, Plus, UserPlus, Check, X, AlertTriangle, 
  Baby, ArrowLeft, Shield, FileText, ChevronRight
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const SitterPools = () => {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [terms, setTerms] = useState(null);
  const [legalStatus, setLegalStatus] = useState(null);
  const [newPool, setNewPool] = useState({ name: "", description: "", invited_user_ids: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [poolsRes, matchesRes, legalRes] = await Promise.all([
        axios.get(`${API}/sitter-pools`),
        axios.get(`${API}/matches`),
        axios.get(`${API}/legal/status`)
      ]);
      setPools(poolsRes.data.pools || []);
      setMatches(matchesRes.data.matches || []);
      setLegalStatus(legalRes.data.status || {});
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${API}/legal/sitter-pool-terms`);
      setTerms(res.data);
      setShowTerms(true);
    } catch (error) {
      toast.error("Failed to load terms");
    }
  };

  const acceptTerms = async () => {
    try {
      await axios.post(`${API}/legal/accept`, { feature: "sitter_pool" });
      toast.success("Terms accepted!");
      setShowTerms(false);
      setLegalStatus(prev => ({
        ...prev,
        sitter_pool: { accepted: true, is_current_version: true }
      }));
    } catch (error) {
      toast.error("Failed to accept terms");
    }
  };

  const createPool = async () => {
    if (!legalStatus?.sitter_pool?.accepted || !legalStatus?.sitter_pool?.is_current_version) {
      fetchTerms();
      return;
    }

    if (!newPool.name.trim()) {
      toast.error("Please enter a pool name");
      return;
    }

    try {
      const res = await axios.post(`${API}/sitter-pools`, newPool);
      toast.success("Sitter Pool created!");
      setShowCreate(false);
      setNewPool({ name: "", description: "", invited_user_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create pool");
    }
  };

  const joinPool = async (poolId) => {
    if (!legalStatus?.sitter_pool?.accepted || !legalStatus?.sitter_pool?.is_current_version) {
      fetchTerms();
      return;
    }

    try {
      await axios.post(`${API}/sitter-pools/${poolId}/join`);
      toast.success("You've joined the pool!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join pool");
    }
  };

  const toggleInvite = (userId) => {
    setNewPool(prev => ({
      ...prev,
      invited_user_ids: prev.invited_user_ids.includes(userId)
        ? prev.invited_user_ids.filter(id => id !== userId)
        : [...prev.invited_user_ids, userId]
    }));
  };

  const hasAcceptedTerms = legalStatus?.sitter_pool?.accepted && legalStatus?.sitter_pool?.is_current_version;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6 pt-8">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/dashboard" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Sitter Pool
          </h1>
        </div>
        <p className="text-teal-100 text-sm ml-9">
          Team up with matched families to share childcare costs
        </p>
      </div>

      {/* Legal Notice Banner */}
      {!hasAcceptedTerms && (
        <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-amber-800 font-medium text-sm">Legal Terms Required</p>
              <p className="text-amber-700 text-xs mt-1">
                Please review and accept the Sitter Pool terms and conditions before creating or joining a pool.
              </p>
              <Button
                onClick={fetchTerms}
                size="sm"
                className="mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="view-terms-btn"
              >
                <FileText className="w-4 h-4 mr-1" />
                View Terms
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="mx-4 mt-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
            <Baby className="w-5 h-5 text-teal-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800">How it works</h3>
            <ul className="text-sm text-slate-600 mt-2 space-y-1">
              <li>1. Create a pool with matched families</li>
              <li>2. Book shared childcare via your preferred sitter</li>
              <li>3. Costs auto-split by number of kids</li>
              <li>4. Kids enjoy group activities!</li>
            </ul>
          </div>
        </div>
      </div>

      {/* My Pools */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-800">My Pools</h2>
          <Button
            onClick={() => hasAcceptedTerms ? setShowCreate(true) : fetchTerms()}
            className="bg-teal-500 hover:bg-teal-600 text-white rounded-full"
            size="sm"
            data-testid="create-pool-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Create Pool
          </Button>
        </div>

        {pools.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No pools yet</p>
            <p className="text-slate-400 text-sm mt-1">Create a pool and invite matched families</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pools.map(pool => (
              <div
                key={pool.pool_id}
                className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{pool.name}</h3>
                      {pool.is_creator && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                          Creator
                        </span>
                      )}
                    </div>
                    {pool.description && (
                      <p className="text-sm text-slate-500 mt-1">{pool.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        {pool.members?.length || 0} members
                      </span>
                      {pool.invited?.length > 0 && (
                        <span className="text-xs text-amber-600">
                          ({pool.invited.length} pending)
                        </span>
                      )}
                    </div>
                  </div>

                  {pool.is_invited && !pool.is_member ? (
                    <Button
                      onClick={() => joinPool(pool.pool_id)}
                      size="sm"
                      className="bg-coral-500 hover:bg-coral-600 text-white"
                      data-testid={`join-pool-${pool.pool_id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Join
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate(`/sitter-pools/${pool.pool_id}`)}
                      size="sm"
                      variant="ghost"
                      className="text-teal-600"
                      data-testid={`view-pool-${pool.pool_id}`}
                    >
                      View
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Pool Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Create Sitter Pool</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Pool Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Park Friends Pool"
                    value={newPool.name}
                    onChange={(e) => setNewPool(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                    data-testid="pool-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    placeholder="What's this pool for?"
                    value={newPool.description}
                    onChange={(e) => setNewPool(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    data-testid="pool-description-input"
                  />
                </div>

                <div>
                  <Label>Invite Matched Families</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {matches.length === 0 ? (
                      <p className="text-sm text-slate-500">No matched families yet. Match with families first!</p>
                    ) : (
                      matches.map(match => (
                        <div
                          key={match.match_id}
                          onClick={() => toggleInvite(match.profile.user_id)}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            newPool.invited_user_ids.includes(match.profile.user_id)
                              ? 'bg-teal-50 border-2 border-teal-500'
                              : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                          }`}
                          data-testid={`invite-${match.profile.user_id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-coral-400 flex items-center justify-center text-white font-bold">
                            {match.profile.parent_name?.[0] || '?'}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{match.profile.parent_name}</p>
                            <p className="text-xs text-slate-500">
                              {match.profile.children?.length || 0} children
                            </p>
                          </div>
                          {newPool.invited_user_ids.includes(match.profile.user_id) && (
                            <Check className="w-5 h-5 text-teal-500" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCreate(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createPool}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid="confirm-create-pool"
                >
                  Create Pool
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {showTerms && terms && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-teal-600" />
                <h2 className="text-xl font-bold text-slate-800">Terms & Conditions</h2>
              </div>
              <p className="text-sm text-slate-500 mt-1">Version {terms.version}</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">
                {terms.terms}
              </pre>
            </div>

            <div className="p-6 border-t bg-slate-50">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowTerms(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={acceptTerms}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid="accept-terms-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  I Accept
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default SitterPools;
